import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

/**
 * Parses a Vimeo reference into a video id and optional private hash.
 *
 * Accepts a raw numeric id, a `vimeo.com/{id}` URL, a private
 * `vimeo.com/{id}/{hash}` URL, a `player.vimeo.com/video/{id}` embed URL, or
 * any of the above with a `?h={hash}` query param. Returns `null` for anything
 * that isn't a recognisable Vimeo reference — the caller then renders no video.
 */
function parseVimeo(input: string): { id: string; hash?: string } | null {
  const trimmed = input.trim();
  if (/^\d+$/.test(trimmed)) return { id: trimmed };

  const idMatch = trimmed.match(/vimeo\.com\/(?:video\/)?(\d+)/i);
  if (!idMatch) return null;
  const id = idMatch[1];

  const queryHash = trimmed.match(/[?&]h=([A-Za-z0-9]+)/);
  const pathHash = trimmed.match(new RegExp(`vimeo\\.com\\/(?:video\\/)?${id}\\/([A-Za-z0-9]+)`, 'i'));
  const hash = queryHash?.[1] ?? pathHash?.[1];

  return hash ? { id, hash } : { id };
}

/**
 * Shared presentational body for the help-tip family (popover, drawer, modal).
 * Renders, in order: an optional responsive 16:9 Vimeo player, an optional
 * title, an optional subtitle, then the projected body content.
 *
 * It is purely presentational — it owns no open/close state. Hosting components
 * pass `[active]` so the Vimeo iframe is only created while the overlay is
 * actually visible (it is never fetched for a closed, but still-mounted, modal).
 *
 * @feature shared
 */
@Component({
  selector: 'app-help-content',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block' },
  template: `
    @if (embedUrl() && active()) {
      <div class="relative mb-4 aspect-video w-full overflow-hidden rounded-lg border border-outline bg-surface-low">
        <iframe
          class="absolute inset-0 h-full w-full"
          [src]="embedUrl()"
          [title]="title() || 'Help video'"
          loading="lazy"
          allow="autoplay; fullscreen; picture-in-picture; clipboard-write"
          allowfullscreen
        ></iframe>
      </div>
    }

    @if (title()) {
      <h2 class="header-font mb-1 text-xl font-semibold text-body">{{ title() }}</h2>
    }
    @if (subtitle()) {
      <p class="mb-3 text-sm text-muted">{{ subtitle() }}</p>
    }

    <div class="text-sm leading-relaxed text-body">
      <ng-content />
    </div>
  `,
})
export class HelpContentComponent {
  private readonly sanitizer = inject(DomSanitizer);

  /** Optional heading shown above the body. */
  readonly title = input<string>();

  /** Optional secondary line shown beneath the title. */
  readonly subtitle = input<string>();

  /** Optional Vimeo reference (id or any Vimeo URL) rendered as a player above the body. */
  readonly videoUrl = input<string>();

  /** Whether the host overlay is currently visible. Gates the iframe so closed-but-mounted hosts don't load Vimeo. @default true */
  readonly active = input(true);

  /** Sanitised Vimeo embed URL, or `null` when no valid Vimeo reference was provided. */
  readonly embedUrl = computed<SafeResourceUrl | null>(() => {
    const raw = this.videoUrl();
    if (!raw) return null;
    const parsed = parseVimeo(raw);
    if (!parsed) return null;
    const src = `https://player.vimeo.com/video/${parsed.id}${parsed.hash ? `?h=${parsed.hash}` : ''}`;
    return this.sanitizer.bypassSecurityTrustResourceUrl(src);
  });
}
